import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import './ServicePage.css';

export default function ServicePage({
  title,
  subtitle,
  description,
  icon,
  color = '#0a66c2',
  features = [],
  process = [],
  benefits = [],
  nextService,
}) {
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="service-page">
      {/* ====== GRAND HERO ====== */}
      <section className="service-hero" style={{ '--service-color': color }}>
        <div className="service-hero__glow" style={{ background: `radial-gradient(circle at 50% 0%, ${color}15 0%, transparent 60%)` }} />
        <div className="container">
          <motion.div 
            className="service-hero__inner-centered"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="hero-eyebrow justify-center" variants={fadeUp}>
              <span className="live-dot" style={{ background: color, boxShadow: `0 0 0 4px ${color}33` }} />
              {subtitle}
            </motion.div>
            
            <motion.h1 className="service-hero__title text-center" variants={fadeUp}>
              {title}
            </motion.h1>
            <motion.p className="service-hero__desc text-center mx-auto" variants={fadeUp}>
              {description}
            </motion.p>
            
            <motion.div className="hero-action-group justify-center" variants={fadeUp}>
              <Link to="/contact" className="btn-solid-primary" style={{ background: color }}>
                获取专属方案
                <ArrowRight size={18} />
              </Link>
              <a href="#features" className="btn-text-link">
                探索核心能力
              </a>
            </motion.div>

            <motion.div className="service-hero__benefits-bar" variants={fadeUp}>
              {benefits.slice(0, 3).map((b, i) => (
                <React.Fragment key={i}>
                  <div className="benefit-bar-item">
                    <CheckCircle size={20} color={color} />
                    <span>{b}</span>
                  </div>
                  {i < 2 && <div className="metric-divider" />}
                </React.Fragment>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ====== FEATURES (BENTO GRID) ====== */}
      <section id="features" className="section-services bg-light">
        <div className="container">
          <motion.div 
            className="section-header-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="section-title-large">服务内容详情</h2>
            <p className="section-subtitle">深度剖析我们的核心技术能力与交付标准</p>
          </motion.div>

          <motion.div 
            className="service-bento"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {features.map((f, i) => {
              return (
                <motion.div 
                  key={i} 
                  className="bento-item" 
                  variants={fadeUp}
                  style={{ '--bento-color': color }}
                >
                  <div className="bento-bg-gradient" />
                  <div className="bento-link-wrapper" style={{ cursor: 'default' }}>
                    <div className="bento-top">
                      <div className="bento-top-left">
                        <div className="bento-icon" style={{ color, background: `${color}15` }}>{f.icon}</div>
                        <h3 className="bento-title">{f.title}</h3>
                      </div>
                      <div className="feature-bento__num" style={{ color: `${color}40` }}>0{i + 1}</div>
                    </div>
                    <div className="bento-content">
                      <div className="bento-desc-wrapper" style={{ flexGrow: 1 }}>
                        <p className="bento-desc">{f.desc}</p>
                      </div>
                      {f.points && (
                        <div className="bento-features" style={{ marginTop: 'auto' }}>
                          {f.points.map((p, j) => (
                            <span key={j} className="bento-feature-tag" style={{ color }}>{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ====== PROCESS ====== */}
      {process.length > 0 && (
        <section className="section-capabilities">
          <div className="container">
            <motion.div 
              className="section-header-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
            >
              <h2 className="section-title-large">专业规范的交付流程</h2>
              <p className="section-subtitle">标准化、透明化的服务链路，确保交付质量与安全可控</p>
            </motion.div>

            <motion.div 
              className="process-timeline-grand"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {process.map((step, i) => (
                <motion.div key={i} className="process-step-grand" variants={fadeUp}>
                  <div className="process-step-grand__num" style={{ color: `${color}40` }}>0{i + 1}</div>
                  <div className="process-step-grand__card bento-item">
                    <div className="bento-top" style={{ marginBottom: '20px' }}>
                      <div className="bento-top-left">
                        <div className="process-step-grand__icon bento-icon" style={{ color, background: `${color}15` }}>{step.icon}</div>
                        <h4 className="bento-title" style={{ fontSize: '1.1rem' }}>{step.title}</h4>
                      </div>
                    </div>
                    <div className="bento-desc-wrapper" style={{ marginBottom: 0 }}>
                      <p className="bento-desc" style={{ marginBottom: 0 }}>{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ====== CTA (BOLD STATEMENT) ====== */}
      <section className="section-statement section-padding">
        <div className="container">
          <motion.div 
            className="statement-content text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="statement-title">
              准备好升级您的<br className="d-md-none" />
              <span style={{ color }}>{title}</span>体系了吗？
            </h2>
            <p className="statement-desc">
              专业团队在线，立即为您提供免费的安全咨询与方案评估。
            </p>
            <Link to="/contact" className="btn-solid-dark">
              获取专属方案
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ====== NEXT SERVICE ====== */}
      {nextService && (
        <div className="service-next">
          <div className="container">
            <Link to={nextService.path} className="service-next__link">
              <span className="service-next__label">探索下一项服务</span>
              <span className="service-next__name">{nextService.name}</span>
              <ArrowRight size={24} className="service-next__icon" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
